import { checkVAT, countries } from '@accountable/jsvat';
import {
  type CalendarDate,
  type CivilityType,
  type Contact,
  CONTACT_TABLE_NAME,
  ContactCommunicationSubscriptionTypes,
  type ContactProfileType,
  ContactType,
  DEFAULT_DIAL_CODE,
  Email,
  type EntityRelation,
  getCleanBankAccountNumber,
  handleDisposableEmail,
  isEmailFromWhitelistedDomains,
  isNilOrEmpty,
  Language,
  type PhoneNumber,
  removeBusinessNumberSeparators,
  typesContainsType,
} from '@rentguru/commons-utils';
import { electronicFormatIBAN, isValidBIC, isValidIBAN } from 'ibantools';
import { isValidPhoneNumber } from 'libphonenumber-js';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { IntlFormatters } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { type AddressFormValues } from 'src/components/ui/Forms/FormField/Address/AddressFields';
import { trimString } from 'src/components/ui/Forms/FormField/utils/yupUtils';
import { ManagementFiltersFormValues } from 'src/components/ui/ManagementFiltersForm/managementFiltersFormUtils';

export interface LanguageFieldFormValues {
  language: Language | null;
}

export interface SendInvitationFieldFormValues {
  sendInvitation: boolean;
}

export interface IndividualNamesFieldsFormValues {
  firstName: string | null;
  lastName: string | null;
}

export interface CompanyInformationFieldsFormValues {
  companyName: string | null;
  commercialName: string | null;
  businessNumber: string | null;
  vat: string | null;
  isVatLiable: boolean;
  mustSendViaPeppol: boolean;
}

export interface IpiFieldFormValues {
  ipiNumber: string | null;
}

export interface JudicialDistrictFieldFormValues {
  judicialDistrict: string | null;
}

export interface AgencyInsuranceFieldFormValues {
  insuranceCompany: string | null;
  insuranceContractNumber: string | null;
}

export interface IndividualIdentityFieldsFormValues {
  birthDate: CalendarDate | null;
  birthPlace: string | null;
  nationalRegister: string | null;
  civility: CivilityType | null;
}

export interface ContactTypeFieldsFormValues {
  profileType: ContactProfileType;
  types: ContactType[];
}

export interface PhoneNumbersFieldsValues {
  phoneNumbers: PhoneNumber[];
}

export interface EmailFieldsFormValues {
  emails: Email[];
  email: string | null;
}

export interface NotesFieldFormValues {
  notes: string | null;
}

export interface LinkedEntityFormValues {
  id: string;
  foreignId: string;
  linkDescription?: string | null;
  contactSubscriptions?: ContactCommunicationSubscriptionTypes[] | null;
}

export type ContactLinkedEntity = {
  linkedEntities: LinkedEntityFormValues[] | null;
};

export type ContactHoaProperties = {
  hoaBuildingIds: string[];
};

export type SupplierIbanValues = {
  id?: string;
  label: string;
  bicNumber: string;
  number: string;
};

export type ContactAccountingValues = {
  paymentTermDays: number | null;
  ibans: SupplierIbanValues[];
};

export type ContactGeneralValues = EmailFieldsFormValues &
  PhoneNumbersFieldsValues &
  AddressFormValues &
  CompanyInformationFieldsFormValues &
  IndividualNamesFieldsFormValues &
  IndividualIdentityFieldsFormValues &
  LanguageFieldFormValues &
  ContactTypeFieldsFormValues &
  SendInvitationFieldFormValues &
  IpiFieldFormValues &
  JudicialDistrictFieldFormValues &
  AgencyInsuranceFieldFormValues &
  NotesFieldFormValues & { originalValues?: Contact };

export enum ContactFormPrefix {
  GENERAL = 'general',
  MANAGEMENT_FILTERS = 'managementFilters',
  LINKED_ENTITIES = 'linkedEntities',
  HOA_PROPERTIES = 'hoaProperties',
  ACCOUNTING = 'accounting',
}

export type ContactValues = {
  [ContactFormPrefix.GENERAL]: ContactGeneralValues;
  [ContactFormPrefix.MANAGEMENT_FILTERS]: ManagementFiltersFormValues;
  [ContactFormPrefix.LINKED_ENTITIES]: ContactLinkedEntity;
  [ContactFormPrefix.HOA_PROPERTIES]: ContactHoaProperties;
  [ContactFormPrefix.ACCOUNTING]: ContactAccountingValues;
};

export const PHONE_NUMBER_LABELS = ['mobile', 'work', 'private', 'school', 'other'];
export const INIT_EMPTY_PHONE_NUMBER: PhoneNumber = {
  label: PHONE_NUMBER_LABELS[0],
  countryCode: DEFAULT_DIAL_CODE,
  number: '',
  isPrimary: true,
};

export const EMAIL_LABELS = ['home', 'work', 'school', 'other'];
export const INIT_EMPTY_EMAIL: Email = { label: EMAIL_LABELS[0], address: '', isPrimary: true };
export const INIT_EMPTY_SUPPLIER_IBAN: SupplierIbanValues = { label: '', bicNumber: '', number: '' };

export const ALL_CONTACT_TYPES = Object.values(ContactType) as ContactType[];
export const DEFAULT_SUGGESTED_CONTACT_TYPES: ContactType[] = [
  ContactType.TENANT,
  ContactType.OWNER,
  ContactType.GUARANTOR,
  ContactType.CONTRACTOR,
  ContactType.HOA,
];

export const FORBIDDEN_ADD_CONTACT_TYPES: ContactType[] = [
  ContactType.JOINT_OWNERSHIP,
  ContactType.MEMBER,
  ContactType.CLIENT,
  ContactType.OTHER,
];

export const FORBIDDEN_EDIT_CONTACT_TYPES: ContactType[] = [ContactType.MEMBER, ContactType.CLIENT];
export const INCOMPATIBLE_CONTACT_TYPE_PAIRS: [ContactType, ContactType][] = [[ContactType.OWNER, ContactType.TENANT]];
export const DISPLAY_IPI_NUMBER_CONTACT_TYPES = [ContactType.CLIENT, ContactType.OWNER];
export const LESSOR_CONTACT_TYPES: ContactType[] = [
  ContactType.OWNER,
  ContactType.JOINT_OWNERSHIP,
  ContactType.MEMBER,
  ContactType.TENANT,
  ContactType.CLIENT,
  ContactType.HOA,
  ContactType.OTHER,
];

export const COMPANY_ONLY_CONTACT_TYPES: ContactType[] = [ContactType.HOA];
export const typesRequireCompanyProfile = (selectedTypes?: ContactType[] | null) =>
  !isNilOrEmpty(selectedTypes) && selectedTypes.every((type) => COMPANY_ONLY_CONTACT_TYPES.includes(type));

export const getStoredBusinessNumber = (businessNumber: string | null): string | null =>
  isNil(businessNumber) ? null : removeBusinessNumberSeparators(businessNumber);

export const SHOW_PEPPOL_CONTACT_TYPES = [ContactType.OWNER, ContactType.CLIENT];
const SHOW_INVITE_CONTACT_TYPES = [ContactType.TENANT, ContactType.OWNER, ContactType.GUARANTOR];
export const canTypesBeInvited = (selectedTypes?: ContactType[]) =>
  SHOW_INVITE_CONTACT_TYPES.some((inviteType) => typesContainsType(selectedTypes ?? [], inviteType));

export const createEmptyRelation = (): Omit<EntityRelation, 'clientId' | 'readId'> => ({
  id: uuidv4(),
  sourceId: '',
  sourceTableName: CONTACT_TABLE_NAME,
  foreignId: '',
  foreignTableName: CONTACT_TABLE_NAME,
});

export const getCompanyLegalEntityValidationSchema = (formatMessage: IntlFormatters['formatMessage']) =>
  Yup.object()
    .shape({
      companyName: Yup.string()
        .nullable()
        .transform(trimString)
        .required(formatMessage({ id: 'contact.detail.error.companyInformation.companyNameMissing' })),
      commercialName: Yup.string().nullable().transform(trimString),
      businessNumber: Yup.string()
        .nullable()
        .transform(getStoredBusinessNumber)
        .min(4, formatMessage({ id: 'contact.detail.error.companyInformation.businessNumberTooShort' })),
      mustSendViaPeppol: Yup.boolean().when('isVatLiable', {
        is: false,
        then: (schema) =>
          schema.oneOf([false], formatMessage({ id: 'contact.detail.error.peppol.mustSendViaPeppol.disabledTooltip' })),
        otherwise: (schema) => schema,
      }),
      vat: Yup.string()
        .nullable()
        .transform(trimString)
        .when('isVatLiable', {
          is: true,
          then: (schema) =>
            schema.required(formatMessage({ id: 'contact.detail.error.companyInformation.vatMissing' })),
          otherwise: (schema) => schema,
        })
        .test('validVat', formatMessage({ id: 'contact.detail.error.companyInformation.invalidVat' }), (value) => {
          if (isNilOrEmpty(value)) return true;
          const vatResult = checkVAT(value, countries);
          return vatResult.isValid;
        }),
    })
    .required();

export const getCompanyInformationValidationSchema = (formatMessage: IntlFormatters['formatMessage']) =>
  getCompanyLegalEntityValidationSchema(formatMessage)
    .concat(
      Yup.object().shape({
        types: Yup.array().of(Yup.string()).required(),
      })
    )
    .required();

export const getContactTypesValidationSchema = () =>
  Yup.object()
    .shape({
      types: Yup.array().of(Yup.string()).required(),
    })
    .required();

export const getEmailsValidationSchema = (
  formatMessage: IntlFormatters['formatMessage'],
  isContactInUp2rent?: boolean
) =>
  Yup.object().shape({
    emails: Yup.array()
      .of(
        Yup.object().shape({
          address: Yup.string()
            .transform(trimString)
            .email(formatMessage({ id: 'contact.detail.error.emails.invalid' }))
            .test(
              'primary-email-isContactInUp2rent',
              formatMessage({ id: 'contact.detail.error.emails.userInUp2rent' }),
              function (value) {
                if (!isContactInUp2rent || !this.parent.isPrimary) return true;
                return !isEmpty(value);
              }
            )
            .test(
              'disposable-email',
              formatMessage({ id: 'contact.detail.error.emails.disposableEmail' }),
              async function (value) {
                const { acceptDisposableEmails } = this.options.context ?? {};
                if (!value || acceptDisposableEmails) return true;
                return await verifyDisposableEmail(value, formatMessage, this.createError, this.path);
              }
            ),
          isPrimary: Yup.boolean(),
        })
      )
      .test('at-least-one-primary', formatMessage({ id: 'contact.detail.error.emails.primaryMissing' }), (emails) => {
        if (isNilOrEmpty(emails)) return true;

        const filledEmails = emails.filter((email) => !isEmpty(email.address));
        if (filledEmails.length > 1) {
          return filledEmails.some((email) => email.isPrimary);
        }

        return true;
      })
      .when('sendInvitation', {
        is: true,
        then: (schema) =>
          schema.test(
            'primary-email-for-invitation',
            formatMessage({ id: 'contact.detail.error.emails.primaryMissing' }),
            (emails) => {
              if (isNilOrEmpty(emails)) return false;
              return emails.some((email) => email.isPrimary && !isEmpty(email.address));
            }
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

export const verifyDisposableEmail = async (
  email: string,
  formatMessage: IntlFormatters['formatMessage'],
  createError: (params?: Yup.CreateErrorOptions | undefined) => Yup.ValidationError,
  path?: string
) => {
  if (!isEmailFromWhitelistedDomains(email)) {
    const result = await handleDisposableEmail(email);

    if (result?.disposable) {
      return createError({
        path: path ?? 'email',
        message: formatMessage({ id: 'signup.disposableEmail' }),
      });
    }
    if (!result?.dns) {
      return createError({
        path: path ?? 'email',
        message: formatMessage({ id: 'signup.noDnsEmail' }),
      });
    }
  }
  return true;
};

export const getIndividualNamesValidationSchema = (formatMessage: IntlFormatters['formatMessage']) =>
  Yup.object().shape({
    firstName: Yup.string()
      .nullable()
      .transform(trimString)
      .required(formatMessage({ id: 'contact.detail.error.individualNames.firstNameMissing' })),
    lastName: Yup.string()
      .nullable()
      .transform(trimString)
      .required(formatMessage({ id: 'contact.detail.error.individualNames.lastNameMissing' })),
  });

export const getPhoneNumberValidationSchema = (formatMessage: IntlFormatters['formatMessage']) =>
  Yup.object().shape({
    phoneNumbers: Yup.array()
      .of(
        Yup.object().shape({
          isPrimary: Yup.boolean().required(),
          label: Yup.string().nullable(),
          countryCode: Yup.string().required(),
          number: Yup.string()
            .transform(trimString)
            .test(
              'valid-phone-number',
              formatMessage({ id: 'contact.detail.error.phoneNumbers.invalid' }),
              function (value) {
                if (!value) return true; // Allow empty phone numbers
                const { countryCode } = this.parent;
                if (!countryCode) return false;

                try {
                  return isValidPhoneNumber(`${countryCode}${value}`);
                } catch (_error) {
                  return false;
                }
              }
            ),
        })
      )
      .test(
        'at-least-one-primary',
        formatMessage({ id: 'contact.detail.error.phoneNumbers.primaryMissing' }),
        (phoneNumbers) => {
          if (!phoneNumbers || phoneNumbers.length === 1) return true;
          return phoneNumbers.some((phone) => phone.isPrimary && !isEmpty(phone.number));
        }
      ),
  });

export const getPaymentTermDaysValidationSchema = (message?: string) =>
  Yup.object().shape({
    paymentTermDays: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .integer(message)
      .min(0, message)
      .max(365, message)
      .nullable(),
  });

const NO_DUPLICATE_INDEX = -1;

export const getElectronicIban = (value: string | null | undefined): string | null =>
  electronicFormatIBAN(getCleanBankAccountNumber(value ?? '')) ?? null;

export const getAccountingValidationSchema = (formatMessage: IntlFormatters['formatMessage']) =>
  getPaymentTermDaysValidationSchema().shape({
    ibans: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string().transform(trimString).optional(),
          bicNumber: Yup.string()
            .transform(trimString)
            .optional()
            .test(
              'validBic',
              formatMessage({ id: 'contact.detail.error.iban.invalidBic' }),
              (value) => isNilOrEmpty(value) || isValidBIC(value)
            )
            .when('number', (number: string, schema) =>
              schema.test(
                'bicCountryMatchesIban',
                formatMessage({ id: 'contact.detail.error.iban.bicCountryMismatch' }),
                (value: string | undefined) => isNilOrEmpty(value) || bicCountryMatchesIban(value ?? '', number)
              )
            ),
          number: Yup.string()
            .transform(trimString)
            .required(formatMessage({ id: 'mandatoryField' }))
            .test('validIban', formatMessage({ id: 'contact.detail.error.iban.invalid' }), (value) => {
              const electronicFormat = getElectronicIban(value);
              return !isNilOrEmpty(electronicFormat) && isValidIBAN(electronicFormat ?? '');
            }),
        })
      )
      .test('uniqueIbans', formatMessage({ id: 'contact.detail.error.iban.duplicate' }), (ibans, testContext) => {
        const cleanNumbers = (ibans ?? []).map((iban) => getElectronicIban(iban.number) ?? '');
        const duplicateIndex = cleanNumbers.findIndex(
          (cleanNumber, index) => !isEmpty(cleanNumber) && cleanNumbers.indexOf(cleanNumber) !== index
        );
        if (duplicateIndex === NO_DUPLICATE_INDEX) {
          return true;
        }
        return testContext.createError({ path: `${testContext.path}[${duplicateIndex}].number` });
      }),
  });

const IBAN_COUNTRY_LENGTH = 2;
const BIC_COUNTRY_START = 4;
const BIC_COUNTRY_END = 6;

export const bicCountryMatchesIban = (bic: string, iban: string | null | undefined): boolean => {
  const electronicIban = getElectronicIban(iban);
  if (isNil(electronicIban) || !isValidIBAN(electronicIban)) {
    return true;
  }
  const ibanCountry = electronicIban.slice(0, IBAN_COUNTRY_LENGTH);
  const bicCountry = bic.trim().toUpperCase().slice(BIC_COUNTRY_START, BIC_COUNTRY_END);
  return ibanCountry === bicCountry;
};

export const normalizePaymentTermDays = (paymentTermDays: number | null | undefined): number | null =>
  isNilOrEmpty(`${paymentTermDays ?? ''}`) ? null : Number(paymentTermDays);
